import { type UserAction, type UsernameCheckParams } from './login-actions';
import type {
  CheckUserNameRedirectType,
  LoginFieldError,
  LoginGlobalError,
  ThunkAction,
} from '../types';
import { CheckUserNamePasswordlessType } from '../types';
import { loginPageId } from '../pages/login/constants';
import {
  addBreadcrumb,
  captureTaggedException,
  setContext,
} from '../utilities/analytics/error-reporting';
import { toChallengeRequest } from '../utilities/recaptcha';
import {
  buildAwsCaptchaHandling,
  getAwsCaptchaTokenValue,
} from '../aws-captcha/aws-captcha.helpers';
import { checkFeatureGate } from '../utilities/feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';

export type LoginV2Action =
  | { type: 'LOGIN_REQUEST_CHECK_USERNAME_V2' }
  | { type: 'LOGIN_REQUEST_CHECK_USERNAME_V2_SUCCEEDED'; userActions: UserAction[] }
  | {
      type: 'LOGIN_REQUEST_CHECK_USERNAME_V2_FAILED';
      error?: LoginGlobalError;
      fieldErrors?: LoginFieldError;
    }
  | { type: 'LOGIN_SELECT_CHECK_USERNAME_V2_USER_ACTION'; userAction: UserAction }
  | { type: 'LOGIN_EXECUTE_CHECK_USERNAME_V2_USER_ACTION'; userAction: UserAction }
  | {
      type: 'LOGIN_EXECUTE_CHECK_USERNAME_V2_USER_ACTION_SUCCEEDED';
      redirectUrl?: string;
      redirectType?: CheckUserNameRedirectType | null;
    }
  | { type: 'LOGIN_EXECUTE_CHECK_USERNAME_V2_USER_ACTION_FAILED'; error: LoginGlobalError }
  | { type: 'LOGIN_RESET_V2' };

export const requestCheckUsernameV2 = (): LoginV2Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_V2',
});

export const requestCheckUsernameV2Succeeded = (userActions: UserAction[]): LoginV2Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_V2_SUCCEEDED',
  userActions,
});

export const requestCheckUsernameV2Failed = ({
  error,
  fieldErrors,
}: {
  error?: LoginGlobalError;
  fieldErrors?: LoginFieldError;
}): LoginV2Action => ({
  type: 'LOGIN_REQUEST_CHECK_USERNAME_V2_FAILED',
  error,
  fieldErrors,
});

export const selectCheckUsernameV2UserAction = (userAction: UserAction): LoginV2Action => ({
  type: 'LOGIN_SELECT_CHECK_USERNAME_V2_USER_ACTION',
  userAction,
});

export const executeCheckUsernameV2UserAction = (userAction: UserAction): LoginV2Action => ({
  type: 'LOGIN_EXECUTE_CHECK_USERNAME_V2_USER_ACTION',
  userAction,
});

export const executeCheckUsernameV2UserActionSucceeded = ({
  redirectUrl,
  redirectType,
}: {
  redirectUrl?: string;
  redirectType?: CheckUserNameRedirectType | null;
} = {}): LoginV2Action => ({
  type: 'LOGIN_EXECUTE_CHECK_USERNAME_V2_USER_ACTION_SUCCEEDED',
  redirectUrl,
  redirectType,
});

export const executeCheckUsernameV2UserActionFailed = (error: LoginGlobalError): LoginV2Action => ({
  type: 'LOGIN_EXECUTE_CHECK_USERNAME_V2_USER_ACTION_FAILED',
  error,
});

export const resetLoginV2 = (): LoginV2Action => ({
  type: 'LOGIN_RESET_V2',
});

export const executeCheckUsernameV2UserActionForBaseLogin =
  (usernameCheckParams: UsernameCheckParams, userAction: UserAction): ThunkAction =>
  async (dispatch, getState) => {
    dispatch(executeCheckUsernameV2UserAction(userAction));

    switch (userAction.action) {
      case 'redirect':
        dispatch(
          executeCheckUsernameV2UserActionSucceeded({
            redirectUrl: userAction.redirect_uri,
            redirectType: userAction.redirect_type,
          })
        );
        break;
      case 'ic_redirect':
        dispatch(
          executeCheckUsernameV2UserActionSucceeded({
            redirectUrl: userAction.redirect_uri,
            redirectType: null,
          })
        );
        break;
      case 'continue_required': {
        usernameCheckParams.analyticsClient.operationalEvent({
          page: loginPageId,
          action: 'checkUsernameStart',
          subject: 'sli',
          attributes: baseLoginV2AnalyticsAttributes,
        });
        const response = await fetch(
          `/rest/v2/check-username/continue${usernameCheckParams.params}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': getState().csrfToken,
            },
            body: JSON.stringify({
              username: usernameCheckParams.username,
              recoveryToken: usernameCheckParams.recoveryToken,
              'challenge-response': toChallengeRequest(usernameCheckParams.challengeResponse),
              destinationOrgId: usernameCheckParams.destinationOrgId,
            }),
          }
        );

        let analyticsAction: 'checkUsernameSuccessful' | 'checkUsernameFailure';

        if (response.ok) {
          const redirectUserAction = (await response.json()) as UserAction;
          if (redirectUserAction.action === 'redirect') {
            dispatch(
              executeCheckUsernameV2UserActionSucceeded({
                redirectUrl: redirectUserAction.redirect_uri,
                redirectType: redirectUserAction.redirect_type,
              })
            );
          } else {
            dispatch(executeCheckUsernameV2UserActionSucceeded());
          }
          analyticsAction = 'checkUsernameSuccessful';
        } else {
          dispatch(executeCheckUsernameV2UserActionFailed({ code: 'unknown' }));
          analyticsAction = 'checkUsernameFailure';
        }

        usernameCheckParams.analyticsClient.operationalEvent({
          page: loginPageId,
          action: analyticsAction,
          subject: 'sli',
          attributes: baseLoginV2AnalyticsAttributes,
        });
        break;
      }
      case 'not_permitted':
        dispatch(executeCheckUsernameV2UserActionFailed({ code: userAction.error_code }));
        break;
      default:
        dispatch(executeCheckUsernameV2UserActionSucceeded());
        break;
    }

    return Promise.resolve();
  };

export const requestCheckUsernameV2ActionSelected = (userAction: UserAction): LoginV2Action =>
  selectCheckUsernameV2UserAction(userAction);

export const requestCheckUsernameV2SelectedActionContinued =
  (usernameCheckParams: UsernameCheckParams, userAction: UserAction): ThunkAction =>
  dispatch =>
    dispatch(executeCheckUsernameV2UserActionForBaseLogin(usernameCheckParams, userAction));

export const requestCheckUsernameV2LookupSucceeded =
  (usernameCheckParams: UsernameCheckParams, userActions: UserAction[]): ThunkAction =>
  dispatch => {
    dispatch(requestCheckUsernameV2Succeeded(userActions));

    if (userActions.length === 0) {
      return Promise.resolve();
    }

    if (userActions.length === 1) {
      const [userAction] = userActions;

      if (userAction.action === 'not_permitted') {
        // Keep not_permitted as a terminal lookup error instead of selecting/executing it.
        return Promise.resolve();
      }

      dispatch(requestCheckUsernameV2ActionSelected(userAction));
      return dispatch(
        requestCheckUsernameV2SelectedActionContinued(usernameCheckParams, userAction)
      );
    }

    return Promise.resolve();
  };

export const requestCheckUsernameV2LookupFailed =
  ({
    error,
    fieldError,
  }: {
    error?: LoginGlobalError;
    fieldError?: LoginFieldError['email'];
  }): ThunkAction =>
  dispatch => {
    dispatch(
      requestCheckUsernameV2Failed({
        error,
        fieldErrors: fieldError ? { email: fieldError } : undefined,
      })
    );

    return Promise.resolve();
  };

export const getPasswordlessFromUserAction = (userAction: UserAction | null) => {
  if (userAction?.action === 'no_action' || userAction?.action === 'social_login') {
    return userAction.passwordless || CheckUserNamePasswordlessType.Disallowed;
  }

  return CheckUserNamePasswordlessType.Disallowed;
};

// Reuse registered base-login event names; distinguish v2 via attributes.
const baseLoginV2AnalyticsAttributes = { source: 'baseLoginV2' };

export const baseLoginCheckUsernameV2 =
  ({
    username,
    params,
    analyticsClient,
    recoveryToken,
    challengeResponse,
    destinationOrgId,
    onShowAwsCaptchaModal,
  }: UsernameCheckParams): ThunkAction =>
  async (dispatch, getState) => {
    dispatch(requestCheckUsernameV2());

    const isAwsCaptchaEnabled = checkFeatureGate(AidFrontendFeatureGate.ENABLE_AWS_WAF_CAPTCHA);
    const shouldIncludeTestCaptchaHeaders = checkFeatureGate(
      AidFrontendFeatureGate.ENABLE_HOST_HEADERS_FOR_AWS_CAPTCHA
    );

    addBreadcrumb({
      category: 'login.check_username_v2',
      message: 'Base login check username v2 API call initiated',
      level: 'info',
      data: {
        hasRecoveryToken: !!recoveryToken,
        hasChallengeResponse: !!challengeResponse,
        challengeType: challengeResponse?.type,
      },
    });

    analyticsClient.operationalEvent({
      page: loginPageId,
      action: 'checkUsernameStart',
      subject: 'sli',
      attributes: baseLoginV2AnalyticsAttributes,
    });

    let response: Response;
    try {
      const awsCaptchaToken = isAwsCaptchaEnabled ? getAwsCaptchaTokenValue() : '';

      response = await fetch(`/rest/v2/check-username${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getState().csrfToken,
          ...(shouldIncludeTestCaptchaHeaders
            ? {
                'test-captcha': 'true',
              }
            : {}),
          ...(isAwsCaptchaEnabled
            ? {
                'aws-waf-token': awsCaptchaToken,
              }
            : {}),
        },
        body: JSON.stringify({
          username,
          recoveryToken,
          'challenge-response': toChallengeRequest(challengeResponse),
          destinationOrgId,
        }),
      });
    } catch (networkError) {
      addBreadcrumb({
        category: 'login.check_username_v2',
        message: 'Network error during base login check-username v2',
        level: 'error',
        data: { isOnline: navigator.onLine },
      });

      captureTaggedException(networkError, {
        capabilities: [
          'capability-group--login',
          'capability-subgroup--username-password-login',
          'capability-name--login-with-username-password',
        ],
        extraProperties: {
          description: 'Network failure during base login check-username v2 API call',
          isOnline: navigator.onLine,
          endpoint: '/rest/v2/check-username',
        },
      });

      dispatch(requestCheckUsernameV2LookupFailed({ error: { code: 'request_error' } }));
      return;
    }

    const awsWafConfig = getState().appConfig.awsWafConfig;

    const retry = () => {
      dispatch(
        baseLoginCheckUsernameV2({
          username,
          params,
          analyticsClient,
          recoveryToken,
          challengeResponse,
          destinationOrgId,
          onShowAwsCaptchaModal,
        })
      );
    };

    const captchaHandling = buildAwsCaptchaHandling(response.status, {
      retry,
      awsWafConfig,
      onShowAwsCaptchaModal,
      breadcrumbTag: 'check-username-v2',
    });

    if (captchaHandling.shouldHandle) {
      captchaHandling.handler();
      return;
    }

    let analyticsAction: 'checkUsernameSuccessful' | 'checkUsernameFailure';
    let analyticsAttributes = { ...baseLoginV2AnalyticsAttributes } as Record<string, unknown>;

    if (response.ok) {
      try {
        const userActions: UserAction[] = await response.json();

        await dispatch(
          requestCheckUsernameV2LookupSucceeded(
            {
              username,
              params,
              analyticsClient,
              recoveryToken,
              challengeResponse,
              destinationOrgId,
              onShowAwsCaptchaModal,
            },
            userActions
          )
        );

        setContext('login.checkUsernameV2', {
          userActionsCount: userActions.length,
          actionTypes: userActions.map(userAction => userAction.action),
          hasRecoveryToken: !!recoveryToken,
          challengeType: challengeResponse?.type || null,
        });

        addBreadcrumb({
          category: 'login.check_username_v2',
          message: 'Base login check username v2 succeeded',
          level: 'info',
          data: {
            userActionsCount: userActions.length,
          },
        });

        analyticsAction = 'checkUsernameSuccessful';
        analyticsAttributes = {
          ...analyticsAttributes,
          userActionsCount: userActions.length,
          actionTypes: userActions.map(userAction => userAction.action),
          requiresActionSelection: userActions.length > 1,
        };
      } catch (parseError) {
        dispatch(requestCheckUsernameV2LookupFailed({ error: { code: 'unknown' } }));
        analyticsAction = 'checkUsernameFailure';
        analyticsAttributes = {
          ...analyticsAttributes,
          reason: 'response_parsing_error',
        };
        captureTaggedException(parseError, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description: 'Base login check-username v2 response OK but failed to parse it',
          },
        });
      }
    } else {
      analyticsAction = 'checkUsernameFailure';
      let responseErrorText: string | null = null;

      addBreadcrumb({
        category: 'login.check_username_v2',
        message: 'Base login check username v2 HTTP error',
        level: 'error',
        data: { statusCode: response.status },
      });

      try {
        analyticsAttributes = {
          ...analyticsAttributes,
          reason: 'http_error',
          httpStatus: response.status,
        };

        if (response.status === 429) {
          dispatch(
            requestCheckUsernameV2LookupFailed({ error: { code: 'too_many_username_requests' } })
          );
        } else if (response.status === 400) {
          const { uiErrors } = await response.json();
          if (uiErrors && uiErrors.length) {
            dispatch(
              requestCheckUsernameV2LookupFailed({ error: { code: uiErrors[0].key || 'unknown' } })
            );
          } else {
            dispatch(requestCheckUsernameV2LookupFailed({ error: { code: 'unknown' } }));
          }
        } else {
          responseErrorText = (await response.text()) || response.statusText;
          const extractedError: LoginFieldError['email'] =
            JSON.parse(responseErrorText).formErrors[0];
          analyticsAttributes = {
            ...analyticsAttributes,
            description: extractedError,
          };
          dispatch(requestCheckUsernameV2LookupFailed({ fieldError: extractedError }));
        }
      } catch (error) {
        const description = 'Failed to handle non-OK response from base login check-username v2';
        captureTaggedException(error, {
          capabilities: [
            'capability-group--login',
            'capability-subgroup--username-password-login',
            'capability-name--login-with-username-password',
          ],
          extraProperties: {
            description,
            responseStatus: response.status,
            responseErrorText,
          },
        });
        analyticsAttributes = {
          ...analyticsAttributes,
          reason: 'unknown',
          description,
        };
        dispatch(requestCheckUsernameV2LookupFailed({ error: { code: 'unknown' } }));
      }
    }

    analyticsClient.operationalEvent({
      page: loginPageId,
      action: analyticsAction,
      subject: 'sli',
      attributes: analyticsAttributes,
    });
  };

export const checkUsernameCaptchaFailureV2 =
  (isOnline: boolean): ThunkAction =>
  dispatch =>
    dispatch(
      requestCheckUsernameV2LookupFailed({
        error: { code: isOnline ? 'captcha' : 'request_error' },
      })
    );
